import {
  apiDelete,
  apiPost,
  apiPut,
} from "../../services/genesisApi/httpsMethods";
import { createNda } from "../../services/ndasApi";
import type { Contact, PartialDeal } from "../../types/deal";
import {
  getChangedRecords,
  getRemovedRecords,
} from "../../utils/compareData";
import {
  forAssetDiff,
  parentAssetId,
  toPositiveIntId,
} from "../../utils/dealAssetUpdateSync";
import { normalizeAssetData } from "../../utils/dealValidation";
import { dealHasNda } from "../../types/nda";
import type { ExtractionNdaDraft } from "../../utils/extractionNda";
import { buildTenantsForAssetUpdate } from "./dealHelperFunctions/dealHelperFunctions";
import {
  describeFailedResponse,
  partialSaveFailure,
  prepareDealPayload,
  type SaveCreateDealResult,
} from "./saveCreateDeal";

type SaveUpdateDealArgs = {
  deal: PartialDeal;
  assets: any[];
  tenants: any[];
  contacts: any[];
  originalAssets: any[];
  originalTenants: any[];
  originalContacts: any[];
  assetIdsNeedingSave: number[];
  referenceContacts: Contact[];
  updatedBy?: string | null;
  extractionId?: number | null;
  acceptExtractionNda?: boolean;
  extractionNdaDraft?: ExtractionNdaDraft | null;
};

function normalizeText(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeNullable(value: unknown) {
  const text = normalizeText(value);
  return text === "" ? null : text;
}

function mapExtractionUpdateErrorMessage(response: {
  status?: number;
  message?: unknown;
}): string | null {
  if (response.status !== 400) return null;

  const rawMessage =
    typeof response.message === "string"
      ? response.message
      : response.message &&
          typeof response.message === "object" &&
          "message" in response.message
        ? String((response.message as { message?: unknown }).message || "")
        : "";

  const normalized = rawMessage.toLowerCase();
  const extractionInvalidOrMissing =
    normalized.includes("invalid extraction_id") ||
    normalized.includes("missing extraction") ||
    (normalized.includes("extraction") && normalized.includes("not found"));

  if (extractionInvalidOrMissing) {
    return "Extraction not found or invalid.";
  }

  const extractionLinkedElsewhere =
    normalized.includes("extraction") &&
    normalized.includes("already linked") &&
    (normalized.includes("different active deal") ||
      normalized.includes("another deal"));

  if (extractionLinkedElsewhere) {
    return "Extraction is already linked to another deal.";
  }

  return null;
}

/**
 * Update-deal save flow:
 * 1) validate + build payload (same as create)
 * 2) PUT deal
 * 3) delete removed assets, PUT changed assets, POST new assets
 * 4) create NDA from extraction draft only if the deal has none
 * 5) link / unlink contacts
 *
 * Once the deal PUT succeeds, later failures are collected and returned as a
 * partial save (`savedDealId`) rather than stopping half-way: the form's
 * asset/tenant baseline is stale after a partial write, so the caller leaves
 * the form instead of letting the user resubmit (which would re-POST assets).
 */
export async function saveUpdateDeal({
  deal,
  assets,
  tenants,
  contacts,
  originalAssets,
  originalTenants,
  originalContacts,
  assetIdsNeedingSave,
  referenceContacts,
  updatedBy,
  extractionId,
  acceptExtractionNda,
  extractionNdaDraft,
}: SaveUpdateDealArgs): Promise<SaveCreateDealResult> {
  const prepared = await prepareDealPayload({
    deal,
    updatedBy,
    extractionId,
  });
  if (!prepared.ok) return prepared;

  const { payload } = prepared;
  const dealId = toPositiveIntId(payload.id) ?? toPositiveIntId(deal.id);
  if (dealId == null) {
    return {
      ok: false,
      message: "Cannot update a deal without an id.",
      title: "Update Deal Error",
    };
  }

  // Validate the NDA draft before anything is written
  const shouldCreateNda =
    !!acceptExtractionNda && !!extractionNdaDraft && !dealHasNda(deal as any);
  if (shouldCreateNda && !extractionNdaDraft?.expiry_date) {
    return {
      ok: false,
      message:
        "NDA expiry date is required when creating a restriction from extraction.",
      title: "NDA Error",
    };
  }

  const updateResponse = await apiPut("deal", dealId, payload);
  if (!updateResponse.success) {
    const extractionErrorMessage =
      mapExtractionUpdateErrorMessage(updateResponse);
    if (extractionErrorMessage) {
      return {
        ok: false,
        message: extractionErrorMessage,
        title: "Extraction Error",
      };
    }
    return { ok: false, apiResponse: updateResponse };
  }

  const failedSteps: string[] = [];
  const assetLabel = (asset: any) =>
    normalizeText(asset?.asset_name) || "Unnamed";

  const removedAssets = getRemovedRecords(assets, originalAssets).filter(
    (asset: any) => toPositiveIntId(asset.id) != null,
  );
  const removedTenants = getRemovedRecords(tenants, originalTenants).filter(
    (tenant: any) => toPositiveIntId(tenant.id) != null,
  );

  for (const asset of removedAssets) {
    const assetId = toPositiveIntId(asset.id);
    if (assetId == null) continue;
    const response = await apiDelete("asset", assetId);
    if (!response.success) {
      failedSteps.push(
        `delete asset "${assetLabel(asset)}" (${describeFailedResponse(response)})`,
      );
    }
  }

  const changedAssetList = getChangedRecords(
    forAssetDiff(assets),
    forAssetDiff(originalAssets),
  );
  const toCreate = changedAssetList.filter(
    (asset) => typeof (asset as any).id !== "number",
  );

  const assetIdsToPut = new Set<number>();
  for (const rawId of assetIdsNeedingSave) {
    const id = toPositiveIntId(rawId);
    if (id != null) assetIdsToPut.add(id);
  }
  for (const asset of changedAssetList) {
    const id = toPositiveIntId((asset as any).id);
    if (id != null) assetIdsToPut.add(id);
  }
  for (const tenant of removedTenants) {
    const parentId = parentAssetId(tenant);
    if (parentId != null) assetIdsToPut.add(parentId);
  }

  for (const assetNumericId of assetIdsToPut) {
    const removedRow = removedAssets.find(
      (asset: any) => toPositiveIntId(asset.id) === assetNumericId,
    );
    if (removedRow) continue;

    const assetToUpdate = assets.find(
      (asset: any) => toPositiveIntId(asset.id) === assetNumericId,
    );
    if (!assetToUpdate || typeof assetToUpdate.asset_name !== "string") {
      continue;
    }

    const tenantsToSend = buildTenantsForAssetUpdate(
      assetToUpdate.asset_name,
      tenants,
      assets,
    );
    const assetResponse = await apiPut("asset", assetNumericId, {
      ...normalizeAssetData(assetToUpdate),
      tenants: tenantsToSend,
    });
    if (!assetResponse.success) {
      failedSteps.push(
        `update asset "${assetLabel(assetToUpdate)}" (${describeFailedResponse(assetResponse)})`,
      );
    }
  }

  for (const row of toCreate) {
    const assetName = (row as any).asset_name;
    if (!assetName || typeof assetName !== "string") continue;
    const tenantsToSend = buildTenantsForAssetUpdate(
      assetName,
      tenants,
      assets,
    );
    const assetResponse = await apiPost("assets", {
      ...normalizeAssetData(row),
      deal_id: dealId,
      tenants: tenantsToSend,
    });
    if (!assetResponse.success) {
      failedSteps.push(
        `create asset "${assetName}" (${describeFailedResponse(assetResponse)})`,
      );
    }
  }

  if (shouldCreateNda && extractionNdaDraft?.expiry_date) {
    const ndaResponse = await createNda({
      restriction_type: extractionNdaDraft.restriction_type,
      expiry_date: extractionNdaDraft.expiry_date,
      applies_to: extractionNdaDraft.applies_to,
      summary: extractionNdaDraft.summary,
      person_responsible_id: extractionNdaDraft.person_responsible_id,
      deal_id: dealId,
    });
    if (!ndaResponse.success) {
      failedSteps.push(`create NDA (${describeFailedResponse(ndaResponse)})`);
    }
  }

  const originalLinkIdsByContact = new Map<number, number[]>();
  const contactLabelById = new Map<number, string>();
  (originalContacts || []).forEach((contact: any) => {
    const contactId = contact?.id;
    if (typeof contactId !== "number") return;
    const name = normalizeText(contact?.contact_name);
    if (name) contactLabelById.set(contactId, `"${name}"`);
    const linkIds: number[] = Array.isArray(contact?.links)
      ? contact.links
          .filter(
            (link: any) =>
              link?.deal_id === dealId && typeof link?.id === "number",
          )
          .map((link: any) => link.id as number)
      : [];
    originalLinkIdsByContact.set(contactId, linkIds);
  });

  const findExistingByEmail = (emailRaw: unknown): Contact | null => {
    const email = normalizeText(emailRaw).toLowerCase();
    if (!email) return null;
    const match = (referenceContacts || []).find(
      (contact: any) =>
        typeof contact?.contact_email === "string" &&
        contact.contact_email.trim().toLowerCase() === email,
    );
    return match || null;
  };

  const keepLinkedContactIds = new Set<number>();
  const processedLinkIds = new Set<number>();

  for (const row of contacts || []) {
    const shouldLink = row?.shouldLink !== false;
    const contactLabel = normalizeText(row?.contact_name) || "Unnamed";

    let contactIdResolved: number | null =
      typeof row?.id === "number" ? row.id : null;
    if (!contactIdResolved) {
      const existing = findExistingByEmail(row?.contact_email);
      if (existing?.id) {
        contactIdResolved = existing.id;
      }
    }

    if (!contactIdResolved) {
      if (!shouldLink) continue;
      const created = await apiPost("contacts", {
        contact_name: normalizeText(row?.contact_name),
        company: normalizeNullable(row?.company),
        contact_email: normalizeNullable(row?.contact_email),
        contact_phone: normalizeNullable(row?.contact_phone),
        contact_title: normalizeNullable(row?.contact_title),
        contact_location: normalizeNullable(row?.contact_location),
        notes: normalizeNullable(row?.notes),
      });
      if (!created.success) {
        failedSteps.push(
          `create contact "${contactLabel}" (${describeFailedResponse(created)})`,
        );
        continue;
      }
      const createdIdRaw = (created.data as any)?.id;
      contactIdResolved =
        typeof createdIdRaw === "number" ? createdIdRaw : null;
      if (!contactIdResolved) {
        failedSteps.push(
          `link contact "${contactLabel}" (contact was created but no id was returned)`,
        );
        continue;
      }
    }

    const existingLinkIds =
      originalLinkIdsByContact.get(contactIdResolved) || [];

    if (shouldLink) {
      keepLinkedContactIds.add(contactIdResolved);
      if (existingLinkIds.length === 0) {
        const linkResponse = await apiPost("contact-link", {
          contact_id: contactIdResolved,
          deal_id: dealId,
          sponsor_id: null,
          entity_id: null,
          source_id: null,
          role: null,
        });
        if (!linkResponse.success) {
          failedSteps.push(
            `link contact "${contactLabel}" (${describeFailedResponse(linkResponse)})`,
          );
        }
      }
    } else {
      for (const linkId of existingLinkIds) {
        if (processedLinkIds.has(linkId)) continue;
        const deleteResponse = await apiDelete("contact-link", linkId);
        // Attempt each link once, so a failure is not retried and reported twice
        processedLinkIds.add(linkId);
        if (!deleteResponse?.success) {
          failedSteps.push(
            `unlink contact "${contactLabel}" (${describeFailedResponse(deleteResponse)})`,
          );
        }
      }
    }
  }

  for (const [contactId, linkIds] of originalLinkIdsByContact.entries()) {
    if (keepLinkedContactIds.has(contactId)) continue;
    for (const linkId of linkIds) {
      if (processedLinkIds.has(linkId)) continue;
      const deleteResponse = await apiDelete("contact-link", linkId);
      processedLinkIds.add(linkId);
      if (!deleteResponse?.success) {
        failedSteps.push(
          `unlink contact ${contactLabelById.get(contactId) || `#${contactId}`} (${describeFailedResponse(deleteResponse)})`,
        );
      }
    }
  }

  if (failedSteps.length > 0) {
    return partialSaveFailure(dealId, "updated", failedSteps);
  }

  return { ok: true, dealId };
}
